import { FluentProvider } from '@fluentui/react-components';
// react-router v8: BrowserRouter/MemoryRouter/HashRouter are root exports of `react-router`.
// The `react-router/dom` subpath only exposes data/RSC helpers (RouterProvider, HydratedRouter, ...).
import { BrowserRouter } from 'react-router';
import { aitaLightTheme } from './theme';
import { AppShell } from '@/components/chrome';
import type { PublicClientApplication } from '@azure/msal-browser';
import { ConfigProvider } from '@/config';
import type { AppConfig } from '@/config';
import { TelemetryProvider } from '@/telemetry';
import { initTelemetry } from '@/telemetry/telemetryService';
import { AuthProvider } from '@/auth';
import { FeatureFlagProvider } from '@/feature-flags';
import { AppRouter } from '@/routing';
import { APP_BASENAME } from '@/routing/basename';
import { ErrorBoundary } from './ErrorBoundary';
import { useCallback, useState, type ErrorInfo } from 'react';

interface AppProps {
  config: AppConfig;
  /** Initialized MSAL instance — present when the resolved auth mode is 'msal' */
  pca?: PublicClientApplication;
  /** Router path to restore after a login redirect round-trip (OAuth state) */
  redirectPath?: string | null;
  /** Error from a failed login-redirect return, surfaced on Landing */
  redirectError?: string | null;
}

export function App({ config, pca, redirectPath, redirectError }: AppProps): React.ReactElement {
  // Consumption state for the one-shot post-login redirect lives HERE — above
  // the ErrorBoundary — because a caught error + "Try again" remounts the
  // router subtree, resetting any consumption state held inside it; the stale
  // redirect would then replay and hijack whatever route the user was on.
  const [redirectConsumed, setRedirectConsumed] = useState(false);
  const handleRedirectConsumed = useCallback(() => setRedirectConsumed(true), []);

  return (
    <ConfigProvider value={config}>
      <ErrorBoundary onError={(error: Error, errorInfo: ErrorInfo) => {
        // Log directly via telemetry service since TelemetryProvider context
        // may not be available if the error originated in a provider.
        const telemetry = initTelemetry(config.appInsightsConnectionString);
        telemetry.trackException(error, {
          componentStack: errorInfo.componentStack ?? 'unknown',
        });
      }}>
        <TelemetryProvider>
          <AuthProvider pca={pca} signInError={redirectError ?? null}>
            <FeatureFlagProvider>
              <FluentProvider theme={aitaLightTheme}>
                <BrowserRouter basename={APP_BASENAME}>
                  <AppShell>
                    <AppRouter
                      redirectPath={redirectConsumed ? null : (redirectPath ?? null)}
                      onRedirectConsumed={handleRedirectConsumed}
                    />
                  </AppShell>
                </BrowserRouter>
              </FluentProvider>
            </FeatureFlagProvider>
          </AuthProvider>
        </TelemetryProvider>
      </ErrorBoundary>
    </ConfigProvider>
  );
}
